import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, EMPTY, of, zip } from 'rxjs';
import { switchMap, catchError, delay, take } from 'rxjs/operators';
import { Action } from '@ngrx/store';
import isEmpty from 'lodash-es/isEmpty';

import {
  createAlertAction, createAlertSuccessAction,
  setActiveAlertsUIAction, loadAlertAction,
  loadAlertSuccessAction, deleteAlertAction,
  deleteAlertSuccessAction, closeDeleteToastAction,
  saveSearchAlertAction, saveSearchAlertSuccessAction
} from '@shared/widgets/store/actions/alerts.action';
import { AlertsService } from '@shared/widgets/services/alerts.service';
import { WidgetsFacade } from '@shared/widgets/store/widgets.facade';
import { isDifferentAlert } from '@shared/widgets/mappers/alert-diff.mapper';
import { AlertPayload, AlertResponse } from '@shared/widgets/entities/alerts';
import { ALERT_DELETE_TOAST_CLOSE_DELAY } from '@shared/widgets/constants/alert.constant';
import { AppFacade } from '@app/app.facade';
import { reloadUserAlertsAction } from '@user-alerts/store/actions/user-alerts.actions';
import { FlightResultsFacade } from '@flight-results/flight-results.facade';
import { StopTypes } from '@shared/flight-itinerary/entities/filters';
import { User } from '@app/modules/core/interfaces/user';
import { SearchBarState } from '@shared/base-search-bar/store/state/search-bar.state';
import { alertPayloadMapper } from '@shared/widgets/mappers/alert-payload.mapper';

@Injectable()
export class AlertsEffects {

  protected alertDeleteToastCloseDelay = ALERT_DELETE_TOAST_CLOSE_DELAY;

  constructor(
    private readonly service: AlertsService,
    private readonly facade: WidgetsFacade,
    private readonly appFacade: AppFacade,
    private readonly flightResultsFacade: FlightResultsFacade,
    private readonly action$: Actions
  ) { }

  loadAlert$: Observable<Action> = createEffect(() => this.action$
    .pipe(
      ofType(loadAlertAction),
      switchMap(() => zip(
        this.facade.alertsEntities$.pipe(take(1)),
        this.facade.alert$.pipe(take(1)),
        this.appFacade.user$.pipe(take(1)),
        this.appFacade.searchData$.pipe(take(1)),
        this.flightResultsFacade.searchPayload$.pipe(take(1))
      ).pipe(
        switchMap(([alerts, alertWidget, user, searchData, searchPayload]) => {
          if (isEmpty(alertWidget) && searchPayload) {
            const directOnly = !!searchPayload && !!searchPayload.Stops
              && !!searchPayload.Stops.find(stop => stop === StopTypes.Stops0);
            const uiActivate = alerts.some(alert => !this.isDifferent(alert,
              this.searchAlertBase(user, searchData, directOnly, 0, alert)));
            const activeAlerts = uiActivate ? alerts.find(alert => !this.isDifferent(alert,
              this.searchAlertBase(user, searchData, directOnly, 0, alert))) : {} as AlertResponse;
            return [
              loadAlertSuccessAction({ data: activeAlerts }),
              setActiveAlertsUIAction({ payload: uiActivate })
            ];
          } else return EMPTY;
        })
      ))
    ));

  createAlert$: Observable<Action> = createEffect(() => this.action$
    .pipe(
      ofType(createAlertAction),
      switchMap(action => zip(
        this.service.createAlert(action.payload),
        this.appFacade.user$
      ).pipe(
        switchMap(([response, user]) => [
          setActiveAlertsUIAction({ payload: true }),
          createAlertSuccessAction({ response: { ...response, ...action.payload } }),
          reloadUserAlertsAction({ user }),
        ]),
        catchError(() => EMPTY)
      ))
    ));

  deleteAlert$: Observable<Action> = createEffect(() => this.action$
    .pipe(
      ofType(deleteAlertAction),
      switchMap(action => this.service.deleteAlert(action.alert).pipe(
        switchMap(() => [
          setActiveAlertsUIAction({ payload: false }),
          deleteAlertSuccessAction()
        ]),
        catchError(() => EMPTY)
      ))
    ));

  deleteAlertSuccess$: Observable<Action> = createEffect(() => this.action$
    .pipe(
      ofType(deleteAlertSuccessAction),
      switchMap(action => of(action).pipe(
        delay(this.alertDeleteToastCloseDelay),
        switchMap(() => [
          closeDeleteToastAction()
        ])
      ))
    ));

  createAlertSuccess$: Observable<Action> = createEffect(() => this.action$
    .pipe(
      ofType(createAlertSuccessAction),
      switchMap(action => of(action).pipe(
        delay(this.alertDeleteToastCloseDelay),
        switchMap(() => [
          closeDeleteToastAction()
        ])
      ))
    ));

  saveSearchAlert$: Observable<Action> = createEffect(() => this.action$
    .pipe(
      ofType(saveSearchAlertAction),
      switchMap(action => this.service.saveAlertSearch(action.payload).pipe(
        switchMap(() => [
          saveSearchAlertSuccessAction({ payload: action.payload })
        ]),
        catchError(() => EMPTY)
      ))
    ));

  private isDifferent(alert: AlertResponse, searchAlert: AlertResponse): boolean {
    return isDifferentAlert(
      { ...alert, id: null },
      { ...searchAlert, oneway: !!alert.oneway, direct_only: !!alert.direct_only }
    );
  }

  private getAlertPayload(user: User, searchData: SearchBarState, directOnly: boolean,
    maxPriceAlert: string | number, flexDates: boolean): AlertPayload {
    return alertPayloadMapper(user, searchData, directOnly, maxPriceAlert, flexDates);
  }

  private searchAlertBase(user: User, searchData: SearchBarState, directOnly: boolean,
    maxPriceAlert: string | number, alert: AlertResponse): AlertResponse {
    return { id: null, ...this.getAlertPayload(user, searchData, directOnly, maxPriceAlert, !!alert.flex_dates) };
  }

}
